import { useState, useRef, useCallback, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import type { EventInput, EventClickArg, DateSelectArg, EventDropArg } from '@fullcalendar/core'
import { Plus, ChevronLeft, ChevronRight, Search } from 'lucide-react'
import toast from 'react-hot-toast'
import { usePageTitle } from '../hooks/usePageTitle'
import {
  appointmentsApi, type Appointment, type AppointmentColor, type UpdateAppointmentDto,
} from '../api/appointments'
import { ConfirmDialog } from '../components/ui/ConfirmDialog'
import { DropdownSelect, type DropdownOption } from '../components/ui/DropdownSelect'
import { AppointmentFormModal } from '../components/appointments/AppointmentFormModal'
import { AppointmentPopup, AppointmentDetailModal } from '../components/appointments/AppointmentDetails'
import { APPOINTMENT_COLORS, COLOR_ORDER, colorOf } from '../components/appointments/appointmentColors'
import { cn } from '../lib/utils'
import { formatDateTime, formatDateOnly } from '../lib/scheduling'

type CalendarView = 'dayGridMonth' | 'timeGridWeek' | 'timeGridDay'

const VIEWS: { value: CalendarView; label: string }[] = [
  { value: 'timeGridDay', label: 'Day' },
  { value: 'timeGridWeek', label: 'Week' },
  { value: 'dayGridMonth', label: 'Month' },
]

interface DragState {
  appointment: Appointment
  newStart: string
  newEnd: string | null
  allDay: boolean
  revert: () => void
}

export function AppointmentsPage() {
  usePageTitle('Appointments')
  const { companyId } = useParams<{ companyId: string }>()
  const qc = useQueryClient()
  const calRef = useRef<FullCalendar>(null)

  const [view, setView] = useState<CalendarView>('timeGridWeek')
  const [currentTitle, setCurrentTitle] = useState('')
  const [colorFilter, setColorFilter] = useState('')

  const [showForm, setShowForm] = useState(false)
  const [prefill, setPrefill] = useState<{ start?: string; end?: string; allDay?: boolean } | undefined>()
  const [editing, setEditing] = useState<Appointment | null>(null)
  const [popup, setPopup] = useState<{ appointment: Appointment; pos: { x: number; y: number } } | null>(null)
  const [detailModal, setDetailModal] = useState<Appointment | null>(null)
  const [dragConfirm, setDragConfirm] = useState<DragState | null>(null)
  const [deleteId, setDeleteId] = useState<string | null>(null)

  const [search, setSearch] = useState('')
  const [searchResults, setSearchResults] = useState<Appointment[]>([])
  const [searchOpen, setSearchOpen] = useState(false)
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // FullCalendar's datesSet drives the fetched window.
  const [range, setRange] = useState(() => {
    const now = new Date()
    return {
      from: new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7).toISOString(),
      to: new Date(now.getFullYear(), now.getMonth(), now.getDate() + 60).toISOString(),
    }
  })

  const { data: appointments = [] } = useQuery({
    queryKey: ['appointments', companyId, range.from, range.to, colorFilter],
    queryFn: () => appointmentsApi.list(companyId!, {
      from: range.from,
      to: range.to,
      color: (colorFilter || undefined) as AppointmentColor | undefined,
    }).then(r => r.data),
    enabled: !!companyId,
  })

  const deleteMut = useMutation({
    mutationFn: (id: string) => appointmentsApi.remove(companyId!, id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['appointments', companyId] })
      toast.success('Appointment deleted')
    },
    onError: () => toast.error('Failed to delete appointment'),
  })

  const moveMut = useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: UpdateAppointmentDto }) =>
      appointmentsApi.update(companyId!, id, dto),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['appointments', companyId] }),
    // The revert is passed per-call in confirmMove — only that closure knows which
    // drag to undo.
    onError: () => toast.error('Failed to move appointment'),
  })

  const events: EventInput[] = appointments.map(a => {
    const spec = colorOf(a.color)
    const base: EventInput = {
      id: a.id,
      title: a.title,
      backgroundColor: spec.hex,
      borderColor: spec.hex,
      textColor: spec.text,
      extendedProps: { appointment: a },
    }
    return a.isAllDay
      ? { ...base, date: a.allDayDate ?? undefined, allDay: true }
      : { ...base, start: a.startAt!, end: a.endAt ?? undefined, allDay: false }
  })

  const handleEventClick = useCallback((arg: EventClickArg) => {
    const appointment = arg.event.extendedProps.appointment as Appointment
    const rect = arg.el.getBoundingClientRect()
    setPopup({ appointment, pos: { x: rect.right + 8, y: rect.top } })
  }, [])

  const handleDateSelect = useCallback((arg: DateSelectArg) => {
    setPrefill({ start: arg.startStr, end: arg.endStr, allDay: arg.allDay })
    setEditing(null)
    setShowForm(true)
  }, [])

  const handleEventDrop = useCallback((arg: EventDropArg) => {
    setDragConfirm({
      appointment: arg.event.extendedProps.appointment as Appointment,
      newStart: arg.event.startStr,
      newEnd: arg.event.endStr || null,
      allDay: arg.event.allDay,
      revert: arg.revert,
    })
  }, [])

  const confirmMove = () => {
    if (!dragConfirm) return
    const { appointment, newStart, newEnd, allDay, revert } = dragConfirm
    const dto: UpdateAppointmentDto = allDay
      ? { isAllDay: true, allDayDate: newStart.slice(0, 10) }
      : { isAllDay: false, startAt: newStart, endAt: newEnd ?? undefined }
    moveMut.mutate({ id: appointment.id, dto }, { onError: () => revert() })
    setDragConfirm(null)
  }

  const cancelMove = () => {
    dragConfirm?.revert()
    setDragConfirm(null)
  }

  const openEdit = (appointment: Appointment) => {
    setPopup(null)
    setDetailModal(null)
    setPrefill(undefined)
    setEditing(appointment)
    setShowForm(true)
  }

  const askDelete = (id: string) => {
    setPopup(null)
    setDetailModal(null)
    setDeleteId(id)
  }

  const handleSearch = (q: string) => {
    setSearch(q)
    if (searchTimer.current) clearTimeout(searchTimer.current)
    if (!q.trim()) { setSearchResults([]); setSearchOpen(false); return }
    searchTimer.current = setTimeout(async () => {
      const res = await appointmentsApi.search(companyId!, q.trim())
      setSearchResults(res.data)
      setSearchOpen(true)
    }, 300)
  }

  const openSearchResult = (a: Appointment) => {
    const target = a.isAllDay && a.allDayDate ? new Date(`${a.allDayDate}T12:00:00`) : new Date(a.startAt ?? a.createdAt)
    calRef.current?.getApi().gotoDate(target)
    setSearch('')
    setSearchResults([])
    setSearchOpen(false)
    setDetailModal(a)
  }

  useEffect(() => {
    if (!searchOpen) return
    const handler = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest('.appointment-search-box')) setSearchOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [searchOpen])

  const switchView = (v: CalendarView) => {
    setView(v)
    calRef.current?.getApi().changeView(v)
  }

  const colorOptions: DropdownOption[] = [
    { value: '', label: 'All colours' },
    ...COLOR_ORDER.map(c => ({ value: c, label: APPOINTMENT_COLORS[c].label })),
  ]

  return (
    <div className="flex flex-col h-full min-h-0 bg-white">
      {/* Toolbar */}
      <div className="flex-shrink-0 px-6 py-3 border-b border-gray-200 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 mr-2">
          <button onClick={() => calRef.current?.getApi().prev()} className="btn-ghost btn-sm p-1.5 rounded-lg" title="Previous">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button onClick={() => calRef.current?.getApi().next()} className="btn-ghost btn-sm p-1.5 rounded-lg" title="Next">
            <ChevronRight className="w-4 h-4" />
          </button>
          <button onClick={() => calRef.current?.getApi().today()} className="btn btn-secondary btn-sm px-3 py-1.5 text-xs">
            Today
          </button>
          <span className="text-sm font-semibold text-gray-900 ml-1 whitespace-nowrap">{currentTitle}</span>
        </div>

        <div className="appointment-search-box relative w-64">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          <input
            value={search}
            onChange={e => handleSearch(e.target.value)}
            onFocus={() => { if (searchResults.length) setSearchOpen(true) }}
            placeholder="Search lead name, phone, email..."
            className="input pl-8"
          />
          {searchOpen && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-[120] max-h-80 overflow-y-auto">
              {searchResults.length === 0 ? (
                <div className="px-3 py-2 text-sm text-gray-400">No matching appointments</div>
              ) : searchResults.map(a => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => openSearchResult(a)}
                  className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-start gap-2"
                >
                  <span
                    className="w-1.5 h-1.5 rounded-full flex-shrink-0 mt-1.5"
                    style={{ backgroundColor: colorOf(a.color).hex }}
                  />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-gray-800 truncate">{a.title}</p>
                    {a.lead && (
                      <p className="text-xs text-gray-400 truncate">
                        {[a.lead.name, a.lead.phone, a.lead.email].filter(Boolean).join(' · ')}
                      </p>
                    )}
                  </div>
                  <span className="text-xs text-gray-400 flex-shrink-0">
                    {a.isAllDay ? formatDateOnly(a.allDayDate) : formatDateTime(a.startAt)}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex-1" />

        <div className="w-36">
          <DropdownSelect options={colorOptions} value={colorFilter} onChange={setColorFilter} placeholder="All colours" />
        </div>

        <div className="flex border border-gray-200 rounded-lg overflow-hidden">
          {VIEWS.map((v, i) => (
            <button
              key={v.value}
              onClick={() => switchView(v.value)}
              className={cn(
                'px-3 py-1.5 text-xs font-medium transition-colors',
                i > 0 && 'border-l border-gray-200',
                view === v.value ? 'bg-primary-600 text-white' : 'text-gray-600 hover:bg-gray-50',
              )}
            >
              {v.label}
            </button>
          ))}
        </div>

        <button
          onClick={() => { setEditing(null); setPrefill(undefined); setShowForm(true) }}
          className="btn btn-primary btn-sm flex items-center gap-1.5"
        >
          <Plus className="w-4 h-4" /> New appointment
        </button>
      </div>

      {/* Calendar */}
      <div className="flex-1 min-h-0 px-4 py-3 overflow-auto calendar-container">
        <FullCalendar
          ref={calRef}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView={view}
          headerToolbar={false}
          height="100%"
          events={events}
          editable
          selectable
          selectMirror
          dayMaxEvents={4}
          weekends
          nowIndicator
          allDaySlot
          slotMinTime="07:00:00"
          slotMaxTime="21:00:00"
          slotDuration="00:30:00"
          eventClick={handleEventClick}
          select={handleDateSelect}
          eventDrop={handleEventDrop}
          datesSet={info => {
            setCurrentTitle(info.view.title)
            setRange({ from: info.startStr, to: info.endStr })
          }}
          eventContent={renderEventContent}
          dayCellClassNames={arg => {
            const day = arg.date.getDay()
            return day === 0 || day === 6 ? ['fc-weekend-cell'] : []
          }}
        />
      </div>

      <AppointmentFormModal
        open={showForm}
        onClose={() => { setShowForm(false); setEditing(null) }}
        companyId={companyId!}
        prefill={prefill}
        editing={editing}
      />

      {popup && (
        <AppointmentPopup
          appointment={popup.appointment}
          anchor={popup.pos}
          companyId={companyId!}
          onClose={() => setPopup(null)}
          onExpand={() => { setDetailModal(popup.appointment); setPopup(null) }}
          onEdit={() => openEdit(popup.appointment)}
          onDelete={() => askDelete(popup.appointment.id)}
        />
      )}

      <AppointmentDetailModal
        appointment={detailModal}
        companyId={companyId!}
        onClose={() => setDetailModal(null)}
        onEdit={openEdit}
        onDelete={askDelete}
      />

      <MoveConfirmDialog state={dragConfirm} onConfirm={confirmMove} onCancel={cancelMove} />

      <ConfirmDialog
        isOpen={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={() => { if (deleteId) deleteMut.mutate(deleteId); setDeleteId(null) }}
        title="Delete this appointment?"
        message="The appointment will be removed from the calendar."
        confirmLabel="Delete"
        isPending={deleteMut.isPending}
      />
    </div>
  )
}

function MoveConfirmDialog({ state, onConfirm, onCancel }: {
  state: DragState | null
  onConfirm: () => void
  onCancel: () => void
}) {
  if (!state) return null
  const label = state.allDay
    ? formatDateOnly(state.newStart.slice(0, 10))
    : formatDateTime(state.newStart)
  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30" onClick={onCancel} />
      <div className="relative bg-white rounded-xl shadow-xl p-5 max-w-sm w-full">
        <p className="font-semibold text-gray-900 mb-1">Move appointment?</p>
        <p className="text-sm text-gray-500 mb-4">
          Move "<span className="font-medium text-gray-700">{state.appointment.title}</span>" to{' '}
          <span className="font-medium text-gray-700">{label}</span>?
        </p>
        <div className="flex justify-end gap-2">
          <button onClick={onCancel} className="btn btn-secondary btn-sm">Cancel</button>
          <button onClick={onConfirm} className="btn btn-primary btn-sm">Move</button>
        </div>
      </div>
    </div>
  )
}

function renderEventContent(arg: { event: { title: string; extendedProps: { appointment?: Appointment } } }) {
  const a = arg.event.extendedProps.appointment
  if (!a) return null
  return (
    <div className="flex items-center gap-1 px-1 py-0.5 min-w-0">
      <span className="text-[11px] font-medium leading-tight truncate">{arg.event.title}</span>
      {a.lead && <span className="text-[10px] opacity-80 truncate hidden sm:inline">· {a.lead.name}</span>}
    </div>
  )
}
